import { foldText } from "./search.js";

/**
 * Pure helpers behind `JsonViewer`: stable node addresses, which containers
 * start open, and Turkish-aware search with highlight ranges. Kept out of the
 * component so they can be unit-tested without a DOM.
 */

/** A node's location from the root: object keys and array indexes. */
export type JsonPath = readonly (string | number)[];

export type JsonKind = "object" | "array" | "string" | "number" | "boolean" | "null";

/**
 * Set-friendly address for a path. JSON keeps key "0" apart from index 0 and
 * a key containing "." apart from two nested keys, which a joined string would not.
 */
export function pathKey(path: JsonPath): string {
  return JSON.stringify(path);
}

export function jsonKind(value: unknown): JsonKind {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  switch (typeof value) {
    case "object":
      return "object";
    case "string":
      return "string";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    default:
      // undefined / bigint / functions never come out of JSON.parse.
      return "null";
  }
}

/** A container's children as [key, value] pairs; empty for primitives. */
export function childrenOf(value: unknown): [string | number, unknown][] {
  if (Array.isArray(value)) return value.map((v, i): [number, unknown] => [i, v]);
  if (value !== null && typeof value === "object") return Object.entries(value);
  return [];
}

/** Keys of every non-empty container shallower than `depth` (the root is depth 0). */
export function containerPaths(value: unknown, depth: number): Set<string> {
  const out = new Set<string>();
  const walk = (node: unknown, path: (string | number)[]): void => {
    if (path.length >= depth) return;
    const children = childrenOf(node);
    if (children.length === 0) return;
    out.add(pathKey(path));
    for (const [key, child] of children) walk(child, [...path, key]);
  };
  walk(value, []);
  return out;
}

export interface JsonSearch {
  /** Nodes whose own key or primitive value contains the query. */
  hits: Set<string>;
  /** Containers above a hit — opened so every hit is actually on screen. */
  ancestors: Set<string>;
}

/**
 * Find the nodes matching `query`. Matching is on folded text (see `foldText`),
 * so "turkiye" finds "Türkiye". Array indexes are positions, not data, and never match.
 */
export function searchJson(value: unknown, query: string): JsonSearch {
  const q = foldText(query).trim();
  const hits = new Set<string>();
  const ancestors = new Set<string>();
  if (q === "") return { hits, ancestors };

  const walk = (node: unknown, path: (string | number)[], key: string | number | undefined): void => {
    const container = node !== null && typeof node === "object";
    const keyHit = typeof key === "string" && foldText(key).includes(q);
    const valueHit = !container && foldText(String(node)).includes(q);
    if (keyHit || valueHit) {
      hits.add(pathKey(path));
      for (let i = 0; i < path.length; i++) ancestors.add(pathKey(path.slice(0, i)));
    }
    for (const [childKey, child] of childrenOf(node)) walk(child, [...path, childKey], childKey);
  };
  walk(value, [], undefined);
  return { hits, ancestors };
}

/**
 * Where `query` occurs in `text`, as [start, end) ranges into the ORIGINAL
 * string. Folding can change length ("ß" → "ss", a decomposed "ü" loses its
 * mark), so each folded character remembers which original character it came
 * from and the ranges are mapped back through that. Overlapping hits merge.
 */
export function matchRanges(text: string, query: string): [number, number][] {
  const q = foldText(query).trim();
  if (q === "") return [];

  let folded = "";
  const origin: number[] = [];
  let at = 0;
  for (const ch of text) {
    const f = foldText(ch);
    for (let k = 0; k < f.length; k++) origin.push(at);
    folded += f;
    at += ch.length;
  }

  const ranges: [number, number][] = [];
  for (let i = folded.indexOf(q); i >= 0; i = folded.indexOf(q, i + 1)) {
    const start = origin[i] ?? 0;
    const last = origin[i + q.length - 1] ?? start;
    const end = last + ((text.codePointAt(last) ?? 0) > 0xffff ? 2 : 1);
    const prev = ranges[ranges.length - 1];
    if (prev && start < prev[1]) prev[1] = Math.max(prev[1], end);
    else ranges.push([start, end]);
  }
  return ranges;
}
